import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { SlideCanvas } from "./SlideCanvas";
import { resolveCaption } from "../lib/captionResolver";
import { Maximize2, Play, Pause, ChevronLeft, ChevronRight, X, Volume2, VolumeX } from "lucide-react";

import { BACKEND } from "../lib/backend";

const TRANSITION_CLASSES = {
  cut: "",
  fade: "transition-opacity duration-500",
  slide: "transition-transform duration-500",
  zoom: "transition-transform duration-500",
};

export default function SlideshowPlayer({ source = "private" }) {
  // `source` is "private" (auth-required /journals/:id) or "public" (/public/journals/:shareId)
  const { id, shareId } = useParams();
  const nav = useNavigate();
  const [journal, setJournal] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const slides = useMemo(
    () => [...(journal?.slides || [])]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((s, i) => ({
        ...s,
        caption: resolveCaption(s, journal, i) || s.caption || "",
      })),
    [journal]
  );
  const tierMap = useMemo(
    () => Object.fromEntries((journal?.tiers || []).map((t) => [t.id, t])),
    [journal]
  );

  useEffect(() => {
    const url = source === "public"
      ? `/public/journals/${shareId}`
      : `/journals/${id}`;
    api.get(url).then(({ data }) => setJournal(data)).catch(() => {
      toast.error("Failed to load journal");
      if (source === "private") nav("/dashboard");
    });
  }, [id, shareId, source, nav]);

  const advance = useCallback(() => {
    setIdx((i) => (slides.length === 0 ? 0 : (i + 1) % slides.length));
  }, [slides.length]);

  useEffect(() => {
    if (!playing || slides.length === 0 || !journal) return;
    const slide = slides[idx];
    const base = journal.base_slide_duration || 5;
    const mult = tierMap[slide?.tier_id]?.duration_multiplier || 1;
    const dur = base * mult * 1000;
    timerRef.current = setTimeout(advance, dur);
    return () => clearTimeout(timerRef.current);
  }, [playing, idx, slides, journal, tierMap, advance]);

  // Audio control
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = muted;
    if (playing) a.play().catch(() => {});
    else a.pause();
  }, [playing, muted]);

  const goFullscreen = async () => {
    try {
      if (containerRef.current?.requestFullscreen) {
        await containerRef.current.requestFullscreen();
      }
    } catch { /* ignore */ }
    setPlaying(true);
    setStarted(true);
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setPlaying(false);
      if (e.key === "ArrowRight") setIdx((i) => Math.min(slides.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  if (!journal)
    return <div className="min-h-screen bg-black grid place-items-center text-zinc-600">Loading…</div>;

  const slide = slides[idx];
  const transition = journal.transition || "fade";
  const txClass = TRANSITION_CLASSES[transition] || TRANSITION_CLASSES.fade;
  const musicUrl = journal.music_url ? `${BACKEND}${journal.music_url}` : "";

  return (
    <div ref={containerRef} className="min-h-screen bg-black text-zinc-50 flex flex-col">
      {musicUrl && <audio ref={audioRef} src={musicUrl} loop preload="auto" />}

      {!started ? (
        <div className="flex-1 grid place-items-center p-8">
          <div className="max-w-lg text-center space-y-6">
            <div className="label-eyebrow">Ready to present</div>
            <h1 className="font-display text-5xl font-bold tracking-tight">{journal.title}</h1>
            <p className="text-zinc-400">
              {slides.length} slides · base {journal.base_slide_duration}s · {transition} transition.
              Higher tiers stay on screen longer.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="enter-fullscreen-btn"
                onClick={goFullscreen}
                className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-md flex items-center gap-2 font-medium"
              >
                <Maximize2 className="w-4 h-4" /> Enter fullscreen & play
              </button>
              <button
                data-testid="start-windowed-btn"
                onClick={() => { setPlaying(true); setStarted(true); }}
                className="border border-zinc-800 hover:bg-zinc-900 px-6 py-3 rounded-md text-zinc-100"
              >
                Start in window
              </button>
            </div>
            {source === "private" && (
              <button
                onClick={() => nav(`/journals/${id}/edit`)}
                className="text-zinc-500 hover:text-zinc-300 text-sm"
                data-testid="back-editor"
              >
                Back to editor
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 relative overflow-hidden">
            {slide ? (
              <div
                key={slide.id}
                className={`absolute inset-0 ${txClass} ${transition === "slide" ? "slide-anim" : ""} ${transition === "zoom" ? "zoom-anim" : ""}`}
              >
                <SlideCanvas
                  slide={slide}
                  tier={tierMap[slide.tier_id]}
                  bgColor={journal.slide_background_color}
                  bgImage={journal.slide_background_image_url}
                />
              </div>
            ) : (
              <div className="absolute inset-0 grid place-items-center text-zinc-600">No slides</div>
            )}
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 bg-zinc-900/80 backdrop-blur-md border border-zinc-800 rounded-full px-3 py-2 flex items-center gap-1 text-sm">
            <button data-testid="prev-slide" onClick={() => setIdx((i) => Math.max(0, i - 1))} className="p-1.5 hover:bg-zinc-800 rounded-full">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button data-testid="play-pause" onClick={() => setPlaying((p) => !p)} className="p-1.5 hover:bg-zinc-800 rounded-full">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button data-testid="next-slide" onClick={() => setIdx((i) => Math.min(slides.length - 1, i + 1))} className="p-1.5 hover:bg-zinc-800 rounded-full">
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="px-3 font-mono text-xs text-zinc-400">
              {slides.length === 0 ? "0/0" : `${idx + 1}/${slides.length}`}
            </div>
            {musicUrl && (
              <button data-testid="mute-toggle" onClick={() => setMuted((m) => !m)} className="p-1.5 hover:bg-zinc-800 rounded-full">
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            )}
            <button
              data-testid="exit-presentation"
              onClick={async () => {
                await exitFullscreen();
                if (source === "private") nav(`/journals/${id}/edit`);
                else { setStarted(false); setPlaying(false); }
              }}
              className="p-1.5 hover:bg-zinc-800 rounded-full"
              title="Exit"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
